import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI, deleteDataAPI } from '../../utils/fetchData';
import { canEditContent, canDeleteContent } from '../../utils/permissions';
import Modal from '../../components/Modal';
import { useModal } from '../../hooks/useModal';
import { OnboardingActionButton } from '../../components/Onboarding/OnboardingActionButton';
import AddToCalendarButton from '../../components/Connectors/AddToCalendarButton';
import {
  getCurrentPhase,
  checkUserData,
  transitionToPhase,
  ONBOARDING_PHASES
} from '../../utils/onboardingStateMachine';
import { getOnboardingDialogue } from '../../utils/interactiveTour';
import ApprovalModal from '../../components/ApprovalModal';
import { useApproval } from '../../hooks/useApproval';

const ViewAll = () => {
    const navigate = useNavigate();
    const { modalConfig, showModal, closeModal } = useModal();
    const { approvalModal, openApprovalModal, closeApprovalModal, sendCatalogForApproval } = useApproval();
    const [activeTab, setActiveTab] = useState('released');
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showImportDropdown, setShowImportDropdown] = useState(false);
    const [showHelpText, setShowHelpText] = useState(true);
    const token = localStorage.getItem('dizeeToken');

    useEffect(() => {
        loadCatalog();
    }, []);

    // Add scroll listener to hide/show help text
    useEffect(() => {
        const handleScroll = (e) => {
            const scrollTop = e.target.scrollTop;
            setShowHelpText(scrollTop < 50); // Hide when scrolled more than 50px
        };

        const scrollContainer = document.querySelector('.catalog-scroll-container');
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            return () => scrollContainer.removeEventListener('scroll', handleScroll);
        }
    }, []);

    const loadCatalog = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
            console.log('Loading catalog with token:', token);
            const response = await getDataAPI('user/getCatalog', token);
            console.log('Catalog API Response:', response);
            console.log('Catalog Response data:', response.data);
            
            if (response.data.status === 200 && response.data.data) {
                // Check if the data is nested deeper
                const catalogData = Array.isArray(response.data.data) ? response.data.data : response.data.data.data;
                console.log('Final catalog data:', catalogData);
                console.log('Final catalog data length:', catalogData?.length);
                
                if (Array.isArray(catalogData)) {
                    console.log('Setting catalog state with:', catalogData.length, 'items');
                    setCatalog(catalogData);
                } else {
                    console.log('Catalog data is not an array, setting empty array');
                    setCatalog([]);
                }
            } else {
                console.log('Catalog API response not successful:', response.data);
                console.log('Setting catalog state to empty array');
                setCatalog([]);
            }
        } catch (error) {
            console.error('Failed to load catalog:', error);
            console.error('Catalog error details:', error.response?.data);
            setCatalog([]);
        } finally {
            setLoading(false);
        }
    };



    const filterCatalogByTab = (catalog, tab) => {
        // Ensure catalog is always an array
        const safeCatalog = Array.isArray(catalog) ? catalog : [];
        console.log(`Filtering catalog for tab: ${tab}, total songs:`, safeCatalog.length);
        
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
        
        // First filter by tab
        let filtered;
        switch(tab) {
            case 'released':
                filtered = safeCatalog.filter(song => {
                    // Song is released if:
                    // 1. Explicitly marked as released, OR
                    // 2. Has a release date that is today or in the past
                    if (song.status === 'released') return true;
                    if (song.release_date) {
                        const releaseDate = new Date(song.release_date);
                        releaseDate.setHours(0, 0, 0, 0);
                        return releaseDate <= currentDate;
                    }
                    return false;
                });
                console.log('Released songs:', filtered.length);
                break;
            case 'upcoming':
                filtered = safeCatalog.filter(song => {
                    // Song is upcoming if:
                    // 1. Explicitly marked as unreleased, OR
                    // 2. Has no release date, OR
                    // 3. Has a release date that is in the future
                    // BUT NOT if it's marked as demo
                    if (song.status === 'demo') return false;
                    if (song.status === 'unreleased') return true;
                    if (!song.release_date) return true;
                    const releaseDate = new Date(song.release_date);
                    releaseDate.setHours(0, 0, 0, 0);
                    return releaseDate > currentDate;
                });
                console.log('Upcoming songs:', filtered.length);
                break;
            case 'demo':
                filtered = safeCatalog.filter(song => {
                    // Song is a demo if explicitly marked as demo
                    return song.status === 'demo';
                });
                console.log('Demo songs:', filtered.length);
                break;
            case 'all':
                filtered = safeCatalog;
                console.log('All songs:', filtered.length);
                break;
            default:
                filtered = safeCatalog;
        }
        
        // Then apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(song => 
                song.song_title?.toLowerCase().includes(query) ||
                song.primary_artist?.toLowerCase().includes(query) ||
                song.album?.toLowerCase().includes(query) ||
                song.genre?.toLowerCase().includes(query)
            );
        }
        
        return filtered;
    };

    const handleBackClick = () => {
        navigate('/');
    };

    const handleItemSelect = (song) => {
        setSelectedItems(prev => {
            const isSelected = prev.some(item => item._id === song._id);
            if (isSelected) {
                return prev.filter(item => item._id !== song._id);
            } else {
                return [...prev, song];
            }
        });
    };

    const handleSelectAll = () => {
        const currentData = getCurrentDisplayData();
        const allSelected = currentData.every(song => selectedItems.some(item => item._id === song._id));
        
        if (allSelected) {
            // Deselect all current items
            setSelectedItems(prev => prev.filter(item => !currentData.some(song => song._id === item._id)));
        } else {
            // Select all current items
            const newSelections = currentData.filter(song => !selectedItems.some(item => item._id === song._id));
            setSelectedItems(prev => [...prev, ...newSelections]);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedItems.length === 0) return;

        setIsDeleting(true);
        const token = localStorage.getItem('dizeeToken');
        let successCount = 0;
        let failCount = 0;
        let deletedReminderCount = 0;

        for (const item of selectedItems) {
            try {
                const response = await deleteDataAPI(`user/deleteCatalog/${item._id}`, token);
                
                if (response.data.status === 200) {
                    // Delete associated reminders after successful catalog deletion
                    const songName = item.song_title || item.track_name || 'Unknown Song';
                    console.log(`🗑️ Deleting reminders for deleted catalog item: ${songName}`);
                    
                    if (window.deleteRemindersForRelease) {
                        const deletedReminders = window.deleteRemindersForRelease(item._id, songName);
                        deletedReminderCount += deletedReminders.length;
                    }
                }
                
                successCount++;
            } catch (error) {
                console.error(`Failed to delete ${item.song_title}:`, error);
                failCount++;
            }
        }

        if (successCount > 0) {
            // Trigger storage events to update reminder app
            localStorage.setItem('catalog_updated', Date.now().toString());
            window.dispatchEvent(new CustomEvent('localStorageUpdate', {
                detail: {
                    key: 'catalog_updated',
                    newValue: Date.now().toString()
                }
            }));
            
            const reminderText = deletedReminderCount > 0 ? ` and ${deletedReminderCount} associated reminders` : '';
            const message = `Successfully deleted ${successCount} song(s)${reminderText}${failCount > 0 ? `, ${failCount} failed` : ''}`;
            showModal('Delete Complete', message, 'success');
            setSelectedItems([]);
            loadCatalog(); // Reload data
        } else {
            showModal('Delete Failed', 'Failed to delete selected songs. Please try again.', 'error');
        }

        setIsDeleting(false);
        setShowDeleteConfirm(false);
    };

    const formatDuration = (duration) => {
        if (!duration) return 'Unknown';
        
        if (typeof duration === 'string') {
            if (duration.includes(':')) return duration;
        }
        
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleEdit = (catalogItem) => {
        navigate(`/catalog/edit/${catalogItem._id}`);
    };

    const handleSendForApproval = (catalogItem) => {
        sendCatalogForApproval(catalogItem, (approvalData) => {
            showModal('Approval Sent', `Catalog approval request sent successfully!`, 'success');
        });
    };

    const getCurrentDisplayData = () => {
        const filtered = filterCatalogByTab(catalog, activeTab);
        
        // Sort by release date - most recent first
        return filtered.sort((a, b) => {
            const dateA = a.release_date ? new Date(a.release_date) : new Date(0); // Use epoch for songs without release date
            const dateB = b.release_date ? new Date(b.release_date) : new Date(0);
            
            // Sort in descending order (most recent first)
            return dateB - dateA;
        });
    };

    const tabs = [
        { id: 'released', label: 'Released' },
        { id: 'upcoming', label: 'Upcoming' },
        { id: 'demo', label: 'Demos' },
        { id: 'all', label: 'All Songs' }
    ];

    const filteredCatalog = getCurrentDisplayData();
    console.log('=== CATALOG FILTERING DEBUG ===');
    console.log('Active tab:', activeTab);
    console.log('Total catalog:', Array.isArray(catalog) ? catalog.length : 'undefined/not array');
    console.log('Filtered catalog:', Array.isArray(filteredCatalog) ? filteredCatalog.length : 'undefined/not array');

    const handleImportOption = (option) => {
        setShowImportDropdown(false);
        switch(option) {
            case 'manual':
                navigate('/catalog/manual-add');
                break;
            case 'single':
                navigate('/catalog/import');
                break;
            case 'bulk':
                navigate('/catalog/bulk-import');
                break;
            case 'agency':
                navigate('/catalog/login-catalog');
                break;
            default:
                break;
        }
    };

    if (loading) {
        return (
            <LayoutHeader>
                <div className="flex justify-center w-full">
                    <div className="w-[390px] h-[80vh] flex flex-col items-center">
                        <div className="text-white text-left py-[50px]">Loading catalog...</div>
                    </div>
                </div>
            </LayoutHeader>
        );
    }

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between items-center">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Catalog</span>
                        <div className="flex items-center gap-4">
                            {selectedItems.length > 0 && canDeleteContent('catalog') && (
                                <>
                                    <span className="text-white text-[12px] font-normal">
                                        Selected ({selectedItems.length})
                                    </span>
                                    <button
                                        onClick={() => setShowDeleteConfirm(true)}
                                        disabled={isDeleting}
                                        className="text-red-500 text-[12px] font-normal cursor-pointer hover:opacity-70 disabled:opacity-50"
                                    >
                                        {isDeleting ? 'Deleting...' : 'Delete'}
                                    </button>
                                </>
                            )}
                            
                            {/* Search and Navigation - Only show when no items are selected */}
                            {selectedItems.length === 0 && (
                                <>
                                    <button 
                                        onClick={() => setShowSearch(!showSearch)}
                                        className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 flex items-center gap-1"
                                    >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        Search Catalog
                                    </button>
                                    
                                    {/* Add Dropdown */}
                                    <div className="relative">
                                        <span 
                                            className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                            onClick={() => setShowImportDropdown(!showImportDropdown)}
                                        >
                                            Add
                                        </span>
                                        
                                        {showImportDropdown && (
                                            <>
                                                {/* Overlay to close dropdown */}
                                                <div 
                                                    className="fixed inset-0 z-10"
                                                    onClick={() => setShowImportDropdown(false)}
                                                />
                                                
                                                {/* Dropdown Menu */}
                                                <div className="absolute left-0 top-6 bg-black border border-[rgba(255,255,255,0.1)] rounded-lg shadow-lg z-20 min-w-[120px] overflow-hidden">
                                                    <div 
                                                        className="px-3 py-2 text-white text-[12px] hover:bg-[rgba(255,255,255,0.1)] cursor-pointer"
                                                        onClick={() => handleImportOption('manual')}
                                                    >
                                                        Manual Add
                                                    </div>
                                                    <div 
                                                        className="px-3 py-2 text-white text-[12px] hover:bg-[rgba(255,255,255,0.1)] cursor-pointer"
                                                        onClick={() => handleImportOption('single')}
                                                    >
                                                        Single Import
                                                    </div>
                                                    <div 
                                                        className="px-3 py-2 text-white text-[12px] hover:bg-[rgba(255,255,255,0.1)] cursor-pointer"
                                                        onClick={() => handleImportOption('bulk')}
                                                    >
                                                        Bulk Import
                                                    </div>
                                                    <div 
                                                        className="px-3 py-2 text-[rgba(255,255,255,0.4)] text-[12px] cursor-not-allowed"
                                                        title="Coming soon"
                                                    >
                                                        Agency Login
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                            
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={() => navigate(-1)}
                            >
                                Go back
                            </span>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="w-full mb-6">
                        <div className="flex">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => {
                                        setActiveTab(tab.id);
                                        setSearchQuery(''); // Clear search when switching tabs
                                        setSelectedItems([]); // Clear selections when switching tabs
                                    }}
                                    className={`flex-1 px-4 py-2 text-[12px] transition-colors relative ${
                                        activeTab === tab.id 
                                            ? 'text-white' 
                                            : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                                    }`}
                                >
                                    {tab.label}
                                    {activeTab === tab.id && (
                                        <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#17A2B8]"></div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Search Interface */}
                    {showSearch && (
                        <div className="w-full px-4 mb-6">
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <svg className="w-4 h-4 text-[#17A2B8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Search Catalog</span>
                                </div>
                                
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Enter song title, artist, album, or genre..."
                                        className="w-full bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#17A2B8]"
                                    />
                                    
                                    {searchQuery && (
                                        <button
                                            onClick={() => setSearchQuery('')}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[rgba(255,255,255,0.5)] hover:text-white transition-colors"
                                        >
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                
                                {searchQuery && (
                                    <p className="text-[rgba(255,255,255,0.7)] text-[10px] mt-2">
                                        Searching across {activeTab} songs...
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Content */}
                    <div className="catalog-scroll-container w-full px-4 flex-1 overflow-y-auto">
                        {filteredCatalog.length === 0 ? (
                            <div className="text-left py-[60px]">
                                <h3 className="text-white text-[12px] font-normal mb-2">
                                    {searchQuery ? `No results for "${searchQuery}"` : `No ${activeTab === 'all' ? 'songs' : activeTab === 'demo' ? 'demos' : activeTab} songs found`}
                                </h3>
                                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                                    {searchQuery ? 
                                        'Try different search terms or check your spelling.' :
                                        (activeTab === 'all' 
                                            ? 'Import your music catalog to see all your tracks here.'
                                            : activeTab === 'demo'
                                            ? 'No demos available. Add demo tracks to see them here.'
                                            : `No ${activeTab} songs available right now.`
                                        )
                                    }
                                </p>
                                <button 
                                    onClick={() => navigate(activeTab === 'upcoming' || activeTab === 'demo' ? '/catalog/manual-add' : '/catalog/bulk-import')}
                                    className="bg-[#17A2B8] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[#20B2AA] transition-colors"
                                >
                                    {activeTab === 'upcoming' ? 'Add Upcoming Release' : activeTab === 'demo' ? 'Add Demo' : 'Import Catalog'}
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Select All Button */}
                                {filteredCatalog.length > 0 && canDeleteContent('catalog') && (
                                    <div className="px-4 mb-4 text-right">
                                        <button
                                            onClick={handleSelectAll}
                                            className="text-[#17A2B8] text-[10px] hover:text-[#20B2AA] transition-colors"
                                        >
                                            {filteredCatalog.every(song => selectedItems.some(item => item._id === song._id)) ? 
                                                'Deselect All' : 'Select All'
                                            }
                                        </button>
                                    </div>
                                )}

                                <div className="grid gap-4">
                                    {filteredCatalog.map((song, index) => {
                                        const isSelected = selectedItems.some(item => item._id === song._id);
                                        return (
                                            <div 
                                                key={index}
                                                className={`bg-[rgba(255,255,255,0.05)] border rounded-lg p-4 transition-all cursor-pointer ${
                                                    isSelected 
                                                        ? 'border-[#17A2B8] bg-[rgba(23,162,184,0.1)]' 
                                                        : 'border-[rgba(255,255,255,0.1)] hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)]'
                                                }`}
                                                onClick={() => canDeleteContent('catalog') ? handleItemSelect(song) : null}
                                                style={{ cursor: canDeleteContent('catalog') ? 'pointer' : 'default' }}
                                            >
                                                <div className="flex items-center gap-4">
                                                    {/* Album Art */}
                                                    <div className="w-12 h-12 bg-[rgba(23,162,184,0.2)] rounded-lg flex items-center justify-center">
                                                        {song.album_artwork ? (
                                                            <img 
                                                                src={song.album_artwork} 
                                                                alt="Album Art" 
                                                                className="w-full h-full object-cover rounded-lg"
                                                            />
                                                        ) : (
                                                            <div className="w-6 h-6 bg-[#17A2B8] rounded"></div>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Song Info */}
                                                    <div className="flex-1">
                                                        <div className="flex items-center justify-between mb-1">
                                                            <h3 className="text-white text-[12px] font-normal">
                                                                {song.song_title || song.name || 'Unknown Song'}
                                                            </h3>
                                                            <div className="flex items-center gap-2">
                                                                {song.release_date && (
                                                                    <AddToCalendarButton
                                                                        eventData={{
                                                                            summary: `${song.song_title || song.name || 'Song'} Release`,
                                                                            date: song.release_date,
                                                                            description: `Release: ${song.song_title || song.name || 'Unknown Song'} by ${song.artist_name || 'Unknown Artist'}${song.album_name ? ` (${song.album_name})` : ''}`
                                                                        }}
                                                                        iconOnly={true}
                                                                        compact={true}
                                                                    />
                                                                )}
                                                                {canEditContent('catalog') && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleEdit(song);
                                                                        }}
                                                                        className="text-[#17A2B8] text-[10px] px-2 py-1 hover:bg-[rgba(23,162,184,0.1)] rounded transition-colors"
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                )}
                                                                {canEditContent('catalog') && activeTab === 'upcoming' && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleSendForApproval(song);
                                                                        }}
                                                                        className="text-[#FF6B35] text-[10px] px-2 py-1 hover:bg-[rgba(255,107,53,0.1)] rounded transition-colors"
                                                                    >
                                                                        Submit
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <p className="text-[rgba(255,255,255,0.7)] text-[10px] mb-2">
                                                            {song.artist_name || 'Unknown Artist'}
                                                        </p>
                                                        {song.album_name && (
                                                            <p className="text-[rgba(255,255,255,0.5)] text-[9px] mb-2">
                                                                {song.album_name}
                                                            </p>
                                                        )}
                                                        <div className="flex items-center gap-2 text-[rgba(255,255,255,0.5)] text-[9px] flex-wrap">
                                                            {song.genre && (
                                                                <span className="bg-[rgba(23,162,184,0.2)] text-[rgba(23,162,184,1)] px-2 py-1 rounded">
                                                                    {song.genre}
                                                                </span>
                                                            )}
                                                            {song.duration && (
                                                                <span className="bg-[rgba(255,255,255,0.1)] px-2 py-1 rounded">
                                                                    {formatDuration(song.duration)}
                                                                </span>
                                                            )}
                                                            {song.isrc && (
                                                                <span className="bg-[rgba(255,255,255,0.1)] px-2 py-1 rounded">
                                                                    ISRC: {song.isrc}
                                                                </span>
                                                            )}
                                                            {song.release_date && (
                                                                <span className="bg-[rgba(255,255,255,0.1)] px-2 py-1 rounded">
                                                                    {new Date(song.release_date).toLocaleDateString()}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {/* Help Text - Hidden */}
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="w-full max-w-[350px] mx-4 transform overflow-hidden rounded-2xl bg-black border-2 border-[#FF2300] p-6 text-left align-middle shadow-xl transition-all">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6 relative">
                            <h3 className="text-white text-[16px] font-normal text-left">Delete Selected Songs</h3>
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="text-gray-400 hover:text-white transition-colors"
                                disabled={isDeleting}
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="mb-8">
                            <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] text-left">
                                Are you sure you want to delete {selectedItems.length} selected song(s)? This action cannot be undone.
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex space-x-4">
                            <button
                                onClick={handleBulkDelete}
                                disabled={isDeleting}
                                className="flex-1 bg-[#FF2300] text-white text-[12px] font-normal py-3 px-4 rounded-lg hover:bg-[#FF2300]/90 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2300] focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                                {isDeleting ? (
                                    <>
                                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                                        Deleting...
                                    </>
                                ) : (
                                    'Delete'
                                )}
                            </button>
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={isDeleting}
                                className="flex-1 bg-transparent border-2 border-[#FF2300] text-white text-[12px] font-normal py-3 px-4 rounded-lg hover:bg-[#FF2300]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2300] focus:ring-opacity-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Custom Modal */}
            <Modal
                isOpen={modalConfig.isOpen}
                onClose={closeModal}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
                showCancel={modalConfig.showCancel}
                confirmText={modalConfig.confirmText}
                cancelText={modalConfig.cancelText}
            />
            
            {/* Onboarding button for Catalog */}
            <OnboardingActionButton
                onClick={async () => {
                    const phase = getCurrentPhase();
                    
                    // Transition to catalog exploring phase if we're in catalog intro
                    if (phase === ONBOARDING_PHASES.CATALOG_INTRO) {
                        const data = await checkUserData();
                        transitionToPhase(ONBOARDING_PHASES.CATALOG_EXPLORING);
                        
                        // Navigate to home to show dialogue
                        navigate('/', {
                            state: {
                                showOnboardingDialogue: true,
                                phase: ONBOARDING_PHASES.CATALOG_EXPLORING,
                                userData: data
                            }
                        });
                    }
                }}
                showInstructions={getCurrentPhase() === ONBOARDING_PHASES.CATALOG_ADD_RELEASE}
            />

            {/* Approval Modal */}
            <ApprovalModal
                isOpen={approvalModal.isOpen}
                onClose={closeApprovalModal}
                itemData={approvalModal.itemData}
                itemType={approvalModal.itemType}
                onApprovalSent={approvalModal.onApprovalSent}
            />
        </LayoutHeader>
    );
};

export default ViewAll; 